import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useUser } from './UserContext';
import { StudentListItem } from '../components/students/StudentList';

interface GuidanceStudentsContextType {
  students: StudentListItem[];
  loading: boolean;
  error: string | null;
  refreshStudents: () => Promise<void>;
  getStudentById: (uid: string) => StudentListItem | null;
}

const GuidanceStudentsContext = createContext<GuidanceStudentsContextType | undefined>(undefined);

export const useGuidanceStudents = () => {
  const context = useContext(GuidanceStudentsContext);
  if (context === undefined) {
    throw new Error('useGuidanceStudents must be used within a GuidanceStudentsProvider');
  }
  return context;
};

interface GuidanceStudentsProviderProps {
  children: ReactNode;
}

export const GuidanceStudentsProvider: React.FC<GuidanceStudentsProviderProps> = ({ children }) => {
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  const fetchStudents = useCallback(async () => {
    // Only fetch if user is authenticated and is a guidance user
    if (!user?.isGuidanceUser || !user?.schoolID) {
      setLoading(false);
      setStudents([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const getStudentsByHighSchool = httpsCallable(functions, 'getStudentsByHighSchool');
      const result = await getStudentsByHighSchool({ schoolID: user.schoolID });
      
      const data = result.data as { success: boolean; students: StudentListItem[]; count: number };
      
      if (data.success && data.students) {
        setStudents(data.students);
      } else {
        setError('Failed to load students');
        setStudents([]);
      }
    } catch (err: any) {
      console.error('Error fetching students:', err);
      setError(err.message || 'Failed to load students');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.isGuidanceUser, user?.schoolID]);

  const refreshStudents = useCallback(async () => {
    await fetchStudents();
  }, [fetchStudents]);

  const getStudentById = useCallback((uid: string): StudentListItem | null => {
    return students.find(student => student.uid === uid) || null;
  }, [students]);

  // Fetch students when user or schoolID changes
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const value: GuidanceStudentsContextType = {
    students,
    loading,
    error,
    refreshStudents,
    getStudentById,
  };

  return (
    <GuidanceStudentsContext.Provider value={value}>
      {children}
    </GuidanceStudentsContext.Provider>
  );
};

